/* -----------------Globals--------------- */
import { useState } from "react";

import { cacheKeys } from "@constants/cache-keys";
/* -----------------Helpers & Hooks--------------- */
import { blackListedStudypackIdsForWeb } from "@constants/common";
import { events } from "@constants/tracking";
import { useExamsFilterDialog } from "@contexts/DialogContext/dialogs/exams-filter";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { images } from "@helpers/images";
import { useAppStore } from "@hooks/useAppStore";
import useExams from "@hooks/useExams";
import { isWeb } from "@memorang/helpers/platform";
import type { ExamDetails, ProductType } from "@memorang/types";
import { PurchaseSuccessApplet } from "@memorang/ui/components/PurchaseSuccessApplet";
import type { PurchaseSuccessAction } from "@memorang/ui/components/PurchaseSuccessApplet/types";
import { useDevice } from "@memorang/ui/useDevice";
import { useQueryClient } from "@tanstack/react-query";
import { trackCustomEvent } from "analytics";
import { useRouter } from "expo-router";
import { useTranslation } from "react-i18next";

/* -----------------Child components--------------- */
import FreeQuizSessionDialog from "@features/auth/components/dialogs/FreeQuizSessionDialog";

export type Purchase = {
	id: string;
	title: string;
	icon: string;
	description?: string;
	codes?: string[];
	bundleType?: ProductType;
	features?: string[];
	isCustomAccess: boolean;
	examId?: string;
	beginStartDate?: string;
	endStartDate?: string;
};

type Props = {
	products: Purchase[];
	examName: string;
	title: string;
	externalPurchase?: boolean;
	examDetails?: ExamDetails;
	successfulPurchaseTitle?: string;
};

export const AccessGrantedView = ({
	products,
	examName,
	title,
	externalPurchase,
	examDetails,
	successfulPurchaseTitle,
}: Props) => {
	const router = useRouter();
	const queryClient = useQueryClient();
	const { t, i18n } = useTranslation();
	const { isMobile } = useDevice();
	const { email } = useAuthContext();

	const { examsList: exams } = useExams();
	const { handleSwitchExamContext } = useExamContext();
	const { showExamsFilter } = useExamsFilterDialog();

	const appFont = useAppStore((store) => store.app.font) || "opensans";
	const ios = useAppStore((store) => store.app.links.ios);
	const android = useAppStore((store) => store.app.links.android);
	const quizEnabled = useAppStore((store) => store.exam.quizEnabled);
	const diagnosticLabel = useAppStore((store) => store.exam.diagnosticLabel);

	const [showFreeQuizDialog, setShowFreeQuizDialog] = useState(false);

	const productId = products[0].id;

	const switchExamContext = async () => {
		if (examDetails) {
			await handleSwitchExamContext(examDetails);
			return true;
		}

		const examDetailsFromExams =
			exams.find((examDetails) =>
				examDetails.exam.bundles.find(
					(bundleDetails) => bundleDetails.bundle.id === productId,
				),
			) ||
			exams.find((examDetails) => examDetails.exam.id === products[0].examId);

		if (examDetailsFromExams) {
			await handleSwitchExamContext(examDetailsFromExams);
			return true;
		}
		// unable to find exam details, allow user to select exam
		trackCustomEvent({
			eventName: events.postRedeemExamContextSwitchFailed,
			examId: products[0].examId,
			email,
		});
		showExamsFilter();
		return false;
	};

	const summary =
		successfulPurchaseTitle ||
		t("purchase.success.summary", {
			count: products.length,
			examName,
			action: externalPurchase
				? t("common.continue")
				: t("purchase.success.actions.goToHome"),
		});

	const hasBlackListedStudyPack = products.some((product) =>
		blackListedStudypackIdsForWeb.includes(Number(product.id)),
	);
	const storeNote =
		isWeb && hasBlackListedStudyPack
			? {
					note: t("purchase.success.storeNote"),
					ios: { badge: images.appStores.ios, href: ios || "" },
					android: { badge: images.appStores.android, href: android || "" },
				}
			: undefined;

	const handleGoToHome = async () => {
		queryClient.invalidateQueries({
			queryKey: [cacheKeys.productMetadata, productId],
		});
		await switchExamContext();
		router.push("/home");
	};

	const handleContinue = async () => {
		const switchExamContextSuccess = await switchExamContext();
		if (!switchExamContextSuccess) {
			return;
		}
		if (isWeb && !isMobile && quizEnabled) {
			router.replace({
				pathname: "/home",
				params: {
					startDiagnostic: "true",
				},
			});
			return;
		}
		const isPracticeTest = products[0].bundleType === "PRACTICE_TEST";
		router.replace(
			isPracticeTest ? "/(protected)/(tabs)/practice-tests" : "/home",
		);
	};

	const isPracticeTestPurchased = products.some(
		(product) => product.bundleType === "PRACTICE_TEST",
	);
	const localizedDiagnosticLabel = diagnosticLabel?.[i18n.language];
	const diagnosticCta = isPracticeTestPurchased
		? t("purchase.success.actions.startDiagnostic", {
				label:
					localizedDiagnosticLabel ||
					t("purchase.success.actions.diagnosticLabel"),
			})
		: localizedDiagnosticLabel
			? t("purchase.success.actions.takeDiagnostic", {
					label: localizedDiagnosticLabel,
				})
			: t("purchase.success.actions.takeFreeDiagnostic");

	const practiceTestProductId = exams
		.find((examDetails) =>
			examDetails.exam.bundles.find(
				(bundleDetails) => bundleDetails.bundle.id === productId,
			),
		)
		?.exam.bundles.find(
			(bundleDetails) => bundleDetails.bundle.bundleType === "PRACTICE_TEST",
		)?.bundle.id;

	const canStartFreeQuiz = Boolean(
		isWeb && practiceTestProductId && quizEnabled,
	);

	const getActions = (): {
		primaryAction: PurchaseSuccessAction;
		secondaryAction?: PurchaseSuccessAction;
	} => {
		if (externalPurchase) {
			return {
				primaryAction: { label: t("common.continue"), onPress: handleContinue },
			};
		}
		if (canStartFreeQuiz) {
			return {
				primaryAction: {
					label: diagnosticCta,
					onPress: async () => {
						await switchExamContext();
						setShowFreeQuizDialog(true);
					},
				},
				secondaryAction: {
					label: t("purchase.success.actions.goToHome"),
					onPress: handleGoToHome,
				},
			};
		}
		return {
			primaryAction: {
				label: t("purchase.success.actions.goToHome"),
				onPress: handleGoToHome,
			},
		};
	};

	const { primaryAction, secondaryAction } = getActions();

	return (
		<>
			<PurchaseSuccessApplet
				title={title}
				summary={summary}
				appFont={appFont}
				products={products.map((product) => ({
					id: product.id,
					title: product.title,
					icon: product.icon,
					description: product.description,
					features: product.isCustomAccess ? undefined : product.features,
				}))}
				storeNote={storeNote}
				primaryAction={primaryAction}
				secondaryAction={secondaryAction}
			/>
			{showFreeQuizDialog && (
				<FreeQuizSessionDialog
					visible={showFreeQuizDialog}
					onClose={(navigate?: boolean) => {
						setShowFreeQuizDialog(false);
						if (navigate) {
							router.push("/home");
						}
					}}
				/>
			)}
		</>
	);
};
